/**
 * Surge Pricing Management API Hooks
 * React hooks for managing surge pricing data
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pricingApiClient } from './pricingApiClient';
import {
  SurgePricing,
  SurgePricingDTO,
  SurgePricingListQueryParams,
  PagedData
} from './surgePricingTypes';
import { ApiResponse } from './types';
import { CityMaster, VehicleCategory } from './pricingTypes';

/**
 * Hook to fetch a single surge pricing rule by ID
 * @param id - The surge pricing ID
 * @param options - Hook options
 */
export function useSurgePricing(
  id: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: SurgePricing) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await pricingApiClient.get<ApiResponse<SurgePricing>>(`/surge-pricing/${id}`);

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as SurgePricing;
  }, [id]);

  return useQuery<SurgePricing>({
    queryKey: ['surge-pricing', id],
    queryFn,
    enabled: options.enabled !== false && !!id,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch paginated list of surge pricing rules
 * @param params - Query parameters (page, size, sort, search)
 * @param options - Hook options
 */
export function useSurgePricings(
  params: SurgePricingListQueryParams = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: PagedData<SurgePricing>) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const queryParams: Record<string, any> = {};

    if (params.page !== undefined) {
      queryParams.page = params.page;
    }
    if (params.size !== undefined) {
      queryParams.size = params.size;
    }
    if (params.search) {
      queryParams.search = params.search;
    }
    if (params.vehicleCategoryId) {
      queryParams.vehicleCategoryId = params.vehicleCategoryId;
    }
    if (params.cityId) {
      queryParams.cityId = params.cityId;
    }
    if (params.isActive !== undefined) {
      queryParams.isActive = params.isActive;
    }
    if (params.dayOfWeek) {
      queryParams.dayOfWeek = params.dayOfWeek;
    }
    if (params.sort && params.sort.length > 0) {
      queryParams.sort = params.sort;
    }

    const response = await pricingApiClient.get<ApiResponse<PagedData<SurgePricing>>>(
      '/surge-pricing/list',
      queryParams
    );

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as PagedData<SurgePricing>;
  }, [params.page, params.size, params.sort, params.search, params.vehicleCategoryId, params.cityId, params.isActive, params.dayOfWeek]);

  return useQuery<PagedData<SurgePricing>>({
    queryKey: ['surge-pricings', params],
    queryFn,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch all surge pricing rules (no pagination)
 * @param options - Hook options
 */
export function useAllSurgePricings(
  options: {
    enabled?: boolean;
    onSuccess?: (data: SurgePricing[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await pricingApiClient.get<ApiResponse<SurgePricing[]>>(`/surge-pricing/list-only`);

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as SurgePricing[];
  }, []);

  return useQuery<SurgePricing[]>({
    queryKey: ['all-surge-pricing'],
    queryFn,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to create a new surge pricing rule
 * @param options - Hook options
 */
export function useCreateSurgePricing(
  options: {
    onSuccess?: (data: ApiResponse<SurgePricing>, variables: SurgePricingDTO) => void;
    onError?: (error: Error, variables: SurgePricingDTO) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<SurgePricing>, Error, SurgePricingDTO>({
    mutationFn: async (surgePricingData: SurgePricingDTO) => {
      const response = await pricingApiClient.post<ApiResponse<SurgePricing>>(
        '/surge-pricing',
        surgePricingData
      );
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate all surge pricing queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['surge-pricings'] });
      queryClient.invalidateQueries({ queryKey: ['all-surge-pricing'] });
      
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to update an existing surge pricing rule
 * @param options - Hook options
 */
export function useUpdateSurgePricing(
  options: {
    onSuccess?: (data: ApiResponse<SurgePricing>, variables: { id: number; data: SurgePricingDTO }) => void;
    onError?: (error: Error, variables: { id: number; data: SurgePricingDTO }) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<SurgePricing>, Error, { id: number; data: SurgePricingDTO }>({
    mutationFn: async ({ id, data }) => {
      const response = await pricingApiClient.put<ApiResponse<SurgePricing>>(
        `/surge-pricing/${id}`,
        data
      );
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate all surge pricing queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['surge-pricings'] });
      queryClient.invalidateQueries({ queryKey: ['all-surge-pricing'] });
      queryClient.invalidateQueries({ queryKey: ['surge-pricing', variables.id] });
      
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to delete a surge pricing rule
 * @param options - Hook options
 */
export function useDeleteSurgePricing(
  options: {
    onSuccess?: (data: ApiResponse<SurgePricing>, surgePricingId: number) => void;
    onError?: (error: Error, surgePricingId: number) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<SurgePricing>, Error, number>({
    mutationFn: async (surgePricingId: number) => {
      const response = await pricingApiClient.delete<ApiResponse<SurgePricing>>(
        `/surge-pricing/${surgePricingId}`
      );
      return response;
    },
    onSuccess: (data, surgePricingId) => {
      // Invalidate all surge pricing queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['surge-pricings'] });
      queryClient.invalidateQueries({ queryKey: ['all-surge-pricing'] });
      queryClient.invalidateQueries({ queryKey: ['surge-pricing', surgePricingId] });
      
      if (options.onSuccess) {
        options.onSuccess(data, surgePricingId);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to toggle surge pricing active status
 * @param options - Hook options
 */
export function useToggleSurgePricingStatus(
  options: {
    onSuccess?: (data: ApiResponse<SurgePricing>, surgePricingId: number) => void;
    onError?: (error: Error, surgePricingId: number) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<SurgePricing>, Error, number>({
    mutationFn: async (surgePricingId: number) => {
      const response = await pricingApiClient.get<ApiResponse<SurgePricing>>(
        `/surge-pricing/toggle-active-status/${surgePricingId}`
      );
      return response;
    },
    onSuccess: (data, surgePricingId) => {
      // Invalidate all surge pricing queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['surge-pricings'] });
      queryClient.invalidateQueries({ queryKey: ['all-surge-pricing'] });
      queryClient.invalidateQueries({ queryKey: ['surge-pricing', surgePricingId] });
      
      if (options.onSuccess) {
        options.onSuccess(data, surgePricingId);
      }
    },
    onError: options.onError,
  });
}

// Re-export existing hooks that are also needed for surge pricing
export { useCities, useVehicleCategories } from './usePricing';